import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Landmark, LogOut, Menu, ShieldCheck, Target, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { InstallAppButton } from "@/components/InstallAppButton";

export const TopBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const out = async () => {
    await logout();
    navigate("/");
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-20 border-b border-emerald-900/10 bg-[#fbf7ef]/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3">
        <Brand to="/painel" />
        <nav className="hidden items-center gap-1 sm:gap-2 md:flex">
          <Link
            data-testid="topbar-goals-link"
            to="/metas"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-xl px-3 text-base font-bold text-emerald-800 transition-colors hover:bg-emerald-50"
          >
            <Target className="h-5 w-5" />
            <span>Metas</span>
          </Link>
          <Link
            data-testid="topbar-pix-link"
            to="/meu-pix"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-xl px-3 text-base font-bold text-emerald-800 transition-colors hover:bg-emerald-50"
          >
            <Landmark className="h-5 w-5" />
            <span>Meu Pix</span>
          </Link>
          {user?.role === "admin" && (
            <Link
              data-testid="topbar-admin-link"
              to="/admin"
              className="inline-flex min-h-[42px] items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2 text-sm font-extrabold text-amber-950 hover:bg-amber-100"
            >
              <ShieldCheck className="h-4 w-4" />
              ADM
            </Link>
          )}
          <InstallAppButton />
          <button
            type="button"
            onClick={out}
            data-testid="topbar-logout-button"
            className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 text-base font-bold text-slate-800 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-800"
          >
            <LogOut className="h-5 w-5" />
            <span>Sair</span>
          </button>
        </nav>
        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-expanded={mobileMenuOpen}
            aria-controls="topbar-mobile-menu"
            aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            data-testid="topbar-mobile-menu-button"
            className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-emerald-200 bg-white text-emerald-800 transition-colors hover:bg-emerald-50"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <button
            type="button"
            onClick={out}
            aria-label="Sair da conta"
            data-testid="topbar-mobile-logout-button"
            className="inline-flex h-12 w-12 items-center justify-center rounded-xl border-2 border-slate-300 bg-white text-slate-800 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-800"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
      {mobileMenuOpen && (
        <nav
          id="topbar-mobile-menu"
          data-testid="topbar-mobile-menu"
          className="border-t border-emerald-900/10 bg-[#fbf7ef] px-5 py-3 md:hidden"
        >
          <div className="mx-auto grid max-w-6xl gap-2">
            <Link
              data-testid="topbar-mobile-goals-link"
              to="/metas"
              onClick={closeMobileMenu}
              className="flex min-h-[52px] items-center gap-3 rounded-xl px-3 text-lg font-bold text-emerald-900 transition-colors hover:bg-emerald-50"
            >
              <Target className="h-5 w-5" />
              Minhas metas
            </Link>
            <Link
              data-testid="topbar-mobile-pix-link"
              to="/meu-pix"
              onClick={closeMobileMenu}
              className="flex min-h-[52px] items-center gap-3 rounded-xl px-3 text-lg font-bold text-emerald-900 transition-colors hover:bg-emerald-50"
            >
              <Landmark className="h-5 w-5" />
              Meu Pix
            </Link>
            {user?.role === "admin" && (
              <Link
                data-testid="topbar-mobile-admin-link"
                to="/admin"
                onClick={closeMobileMenu}
                className="flex min-h-[52px] items-center gap-3 rounded-xl bg-amber-50 px-3 text-lg font-bold text-amber-950 transition-colors hover:bg-amber-100"
              >
                <ShieldCheck className="h-5 w-5" />
                Painel ADM
              </Link>
            )}
            <InstallAppButton mobile />
          </div>
        </nav>
      )}
    </header>
  );
};
